import { useEffect, useState } from "react";
import api, { fmtErr } from "../../lib/api";
import { toast } from "sonner";
import { StatCard } from "./DeskDashboard";
import { BarChart3, Ticket, CheckCircle2, Timer, Gauge } from "lucide-react";

const P_COLOR = { P1: "#EF4444", P2: "#F97316", P3: "#F59E0B", P4: "#94A3B8" };

export default function DeskAnalytics() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/helpdesk/analytics?days=${days}`).then((r) => setData(r.data)).catch((e) => toast.error(fmtErr(e)));
  }, [days]);

  const maxDay = data ? Math.max(...data.per_day.map((d) => Math.max(d.created, d.resolved)), 1) : 1;
  const catMax = data ? Math.max(...Object.values(data.by_category || { x: 0 }), 1) : 1;
  const t = data?.totals;

  return (
    <div data-testid="desk-analytics-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
        <h1 className="text-2xl font-bold font-heading flex items-center gap-2"><BarChart3 size={22} /> Analytics</h1>
        <select data-testid="analytics-days-select" value={days} onChange={(e) => setDays(Number(e.target.value))}
          className="bg-white border border-neutral-300 rounded-md px-3 py-2 text-sm outline-none">
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </select>
      </div>
      <p className="text-sm text-slate-500 mb-5">Ticket volume, SLA compliance and agent performance.</p>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <StatCard icon={Ticket} label="Created" value={t?.created ?? "—"} tone="slate" testId="an-created" />
        <StatCard icon={CheckCircle2} label="Resolved" value={t?.resolved ?? "—"} tone="emerald" testId="an-resolved" />
        <StatCard icon={Ticket} label="Open now" value={t?.open_now ?? "—"} tone="cyan" testId="an-open" />
        <StatCard icon={Timer} label="Avg resolution" value={t?.avg_resolution_hours != null ? `${t.avg_resolution_hours}h` : "—"} tone="amber" testId="an-avg" />
        <StatCard icon={Gauge} label="SLA met" value={t?.sla_met_pct != null ? `${t.sla_met_pct}%` : "—"}
          tone={t?.sla_met_pct == null ? "slate" : t.sla_met_pct >= 90 ? "emerald" : t.sla_met_pct >= 70 ? "amber" : "red"} testId="an-sla" />
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-5 mb-6" data-testid="an-volume-chart">
        <div className="flex items-center gap-4 mb-4">
          <h3 className="font-semibold font-heading text-sm">Created vs resolved per day</h3>
          <span className="flex items-center gap-1.5 text-xs text-slate-500"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: "var(--brand-primary)" }} /> Created</span>
          <span className="flex items-center gap-1.5 text-xs text-slate-500"><span className="w-2.5 h-2.5 rounded-sm bg-emerald-500" /> Resolved</span>
        </div>
        <div className="flex items-end gap-[2px] h-36">
          {(data?.per_day || []).map((d) => (
            <div key={d.date} className="flex-1 flex items-end gap-[1px] group relative" title={`${d.date}: ${d.created} created · ${d.resolved} resolved`}>
              <div className="flex-1 rounded-t-sm" style={{ height: `${(d.created / maxDay) * 100}%`, background: "var(--brand-primary)", minHeight: d.created ? 3 : 0 }} />
              <div className="flex-1 bg-emerald-500 rounded-t-sm" style={{ height: `${(d.resolved / maxDay) * 100}%`, minHeight: d.resolved ? 3 : 0 }} />
            </div>
          ))}
        </div>
        <div className="flex justify-between text-[10px] text-slate-400 mt-1">
          <span>{data?.per_day?.[0]?.date}</span><span>{data?.per_day?.[data.per_day.length - 1]?.date}</span>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4 mb-6">
        <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="an-by-category">
          <h3 className="font-semibold font-heading text-sm mb-4">By category</h3>
          {Object.entries(data?.by_category || {}).sort((a, b) => b[1] - a[1]).map(([c, n]) => (
            <div key={c} className="mb-2.5">
              <div className="flex justify-between text-xs mb-1"><span className="font-medium">{c}</span><span className="text-slate-400">{n}</span></div>
              <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${(n / catMax) * 100}%`, background: "var(--brand-accent)" }} />
              </div>
            </div>
          ))}
          {!Object.keys(data?.by_category || {}).length && <p className="text-xs text-slate-400">No tickets in this period.</p>}
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="an-by-priority">
          <h3 className="font-semibold font-heading text-sm mb-4">By priority</h3>
          {["P1", "P2", "P3", "P4"].map((p) => {
            const n = data?.by_priority?.[p] || 0;
            const pm = Math.max(...Object.values(data?.by_priority || { x: 0 }), 1);
            return (
              <div key={p} className="flex items-center gap-3 mb-2.5">
                <span className="w-8 text-xs font-semibold">{p}</span>
                <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${(n / pm) * 100}%`, background: P_COLOR[p] }} />
                </div>
                <span className="w-6 text-xs text-slate-500 text-right">{n}</span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="an-agents">
        <h3 className="font-semibold font-heading text-sm px-5 pt-5 pb-3">Agent performance (resolved in period)</h3>
        {data?.agents?.length ? (
          <table className="min-w-full divide-y divide-slate-200 text-sm text-left">
            <thead><tr className="bg-slate-50">
              {["Agent", "Resolved", "Avg resolution"].map((h) => <th key={h} className="px-5 py-2.5 font-medium text-slate-500 uppercase tracking-wider text-xs">{h}</th>)}
            </tr></thead>
            <tbody className="divide-y divide-slate-100">
              {data.agents.map((a) => (
                <tr key={a.name}>
                  <td className="px-5 py-2.5 font-medium">{a.name}</td>
                  <td className="px-5 py-2.5">{a.resolved}</td>
                  <td className="px-5 py-2.5 text-slate-500">{a.avg_hours != null ? `${a.avg_hours}h` : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : <p className="px-5 pb-5 text-xs text-slate-400">No resolved tickets in this period.</p>}
      </div>
    </div>
  );
}
